/**
 * Site-address helpers shared by the wizard and the site settings. Moved out of
 * routes/website-wizard.tsx so the wizard's own steps can use them without
 * importing the wizard back.
 */

/**
 * Validate user-typed input and produce a clean origin URL when possible.
 * Returns either {ok: true, origin} or {ok: false, reason} so callers can
 * show a specific error message and gate UI on `ok`.
 *
 * Allowed: letters, digits, hyphens, dots, plus an optional path. A site must
 * have a TLD (something after a dot) — IP literals and bare hosts are rejected
 * because the proxy targets only real public domains.
 */
export type UrlCheck =
	{ ok: true; origin: string } | { ok: false; reason: 'empty' | 'invalid-chars' | 'missing-tld' | 'malformed' }

export function checkUrl(input: string): UrlCheck {
	const trimmed = input.trim()
	if (!trimmed) {
		return { ok: false, reason: 'empty' }
	}

	// Strip scheme for character check; URL hostnames don't have most punctuation.
	const stripped = trimmed.replace(/^https?:\/\//i, '')
	// Only allow chars valid in domain + path. Reject spaces, @, !, etc.
	if (!/^[a-zA-Z0-9._\-/?#=&%:+]+$/.test(stripped)) {
		return { ok: false, reason: 'invalid-chars' }
	}

	const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
	let u: URL
	try {
		u = new URL(withScheme)
	} catch {
		return { ok: false, reason: 'malformed' }
	}
	const host = u.hostname
	if (!host) {
		return { ok: false, reason: 'malformed' }
	}

	// Require a real TLD: at least one dot, with a 2+ char suffix that looks
	// like letters (so `foo.1` or `foo.` is rejected, but `foo.io` is fine).
	if (!/\.[a-zA-Z]{2,}$/.test(host)) {
		return { ok: false, reason: 'missing-tld' }
	}

	return { ok: true, origin: u.origin }
}

/** Convenience wrapper used by callers that don't need the reason. */
export function normalizeUrl(input: string): string | null {
	const r = checkUrl(input)
	return r.ok ? r.origin : null
}
